import { useCallback } from "react";

import { useTerminalContext } from "../contexts/TerminalContext";
import type { ReadyConfig } from "../shared/types";

/**
 * A hook that provides actions and state for managing the readyConfig history (undo/redo).
 */
export const useReadyConfigHistory = () => {
  const {
    setReadyConfig,
    readyConfigHistory,
    setReadyConfigHistory,
    currentReadyHistoryIndex,
    setCurrentReadyHistoryIndex,
  } = useTerminalContext();

  const updateReadyConfig = useCallback(
    (newConfig: ReadyConfig) => {
      const newHistory = readyConfigHistory.slice(0, currentReadyHistoryIndex + 1);
      newHistory.push(newConfig);
      setReadyConfig(newConfig);
      setReadyConfigHistory(newHistory);
      setCurrentReadyHistoryIndex(newHistory.length - 1);
    },
    [
      readyConfigHistory,
      currentReadyHistoryIndex,
      setReadyConfig,
      setReadyConfigHistory,
      setCurrentReadyHistoryIndex,
    ]
  );

  const undo = useCallback(() => {
    if (currentReadyHistoryIndex > 0) {
      const newIndex = currentReadyHistoryIndex - 1;
      setCurrentReadyHistoryIndex(newIndex);
      setReadyConfig(readyConfigHistory[newIndex]);
    }
  }, [readyConfigHistory, currentReadyHistoryIndex, setReadyConfig, setCurrentReadyHistoryIndex]);

  const redo = useCallback(() => {
    if (currentReadyHistoryIndex < readyConfigHistory.length - 1) {
      const newIndex = currentReadyHistoryIndex + 1;
      setCurrentReadyHistoryIndex(newIndex);
      setReadyConfig(readyConfigHistory[newIndex]);
    }
  }, [readyConfigHistory, currentReadyHistoryIndex, setReadyConfig, setCurrentReadyHistoryIndex]);

  const canUndo = currentReadyHistoryIndex > 0;
  const canRedo = currentReadyHistoryIndex < readyConfigHistory.length - 1;

  return {
    updateReadyConfig,
    undo,
    redo,
    canUndo,
    canRedo,
  };
};
